// code-guardian-ignore-duplicates: the reference master-page shell (tab kept in ?tab=, selectTab, link-search clean-up, tab strip with the pending-requests badge + action slot) that the other tabbed pages mirror on purpose
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Store, Tags, SlidersHorizontal, Layers, ClipboardCheck, UserCog } from 'lucide-react';
import StoresPage from './StoresPage';
import StoreCategoriesPage from './StoreCategoriesPage';
import StoreCustomFieldsPage from './StoreCustomFieldsPage';
import StoreTradesPage from './StoreTradesPage';
import StoreRequestsPage from './StoreRequestsPage';
import ManagerStoreAccessPage from './ManagerStoreAccessPage';
import { managerStoreAccessAPI, storeRequestsAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { dashboardPath } from '../navigation/dashboards';

// The address values a link can use to open this page on one row.
const LINK_KEYS = ['employeeCode', 'storeCode', 'q'] as const;

/**
 * Master Data → Stores.
 *
 * The store roster and the category master shipped as two separate Master Data
 * pages, but a category only exists to be assigned to a store — you edit one to
 * change the other, and the two nav entries meant leaving the page to do it. They
 * are now two tabs of one page.
 *
 * Both tabs are the existing pages in `embedded` mode — same convention as
 * BuddyPoolPage/OnboardingSignoffFormPage inside ShadowTrainingPage — so neither
 * is rewritten or forked. Each keeps its own data fetching, filters and modals;
 * this shell supplies only the heading and the tab strip.
 *
 * Hiring is no longer a tab here (15 Oct 2026): it is its own dashboard, Dashboard › Hiring.
 * The Stores tab keeps a small Hiring column and the row actions Raise hiring (or Ask for
 * hiring) and Link. An old address with ?tab=hiring opens the Hiring dashboard.
 */

type Tab = 'stores' | 'categories' | 'trades' | 'custom-fields' | 'requests' | 'manager-access';

const TABS: { key: Tab; label: string; icon: typeof Store; blurb: string }[] = [
  {
    key: 'stores',
    label: 'Stores',
    icon: Store,
    blurb: 'The store roster — location, category, status, and the employees deployed to each one.',
  },
  {
    key: 'categories',
    label: 'Store Categories',
    icon: Tags,
    blurb:
      'The category master every store is graded against — payout range, SEED level, eligibility and the minimum headcount below which a store counts as vacant.',
  },
  {
    key: 'trades',
    label: 'Trades',
    icon: Layers,
    blurb:
      'The trade list a store can be classified as. Governed here rather than inferred from existing data, so a new trade is selectable before any store uses it — and a typo in a bulk upload no longer becomes a permanent option.',
  },
  {
    key: 'custom-fields',
    label: 'Custom Fields',
    icon: SlidersHorizontal,
    blurb:
      'Extra details this project wants on every store, defined by you — the field name, its input type, and the exact values users may pick. They appear on the store form, in the bulk upload template and in the Download.',
  },
  {
    key: 'requests',
    label: 'Store Requests',
    icon: ClipboardCheck,
    blurb:
      'Stores submitted from the field on the mobile app. Approving one creates it on the Stores tab and maps the employee who raised it to that store.',
  },
  {
    key: 'manager-access',
    label: 'Manager Access',
    icon: UserCog,
    blurb:
      'Stores a manager looks after beyond their own team — by trade, category, region, other store details or a list of stores. They are added to that manager’s store lists and store visits, and the managers above them see them too. Managers without a rule see exactly what they see today.',
  },
];

/** The tab that moved: Stores › Hiring is Dashboard › Hiring since 15 Oct 2026. */
const MOVED_HIRING_TAB = 'hiring';

type Access = 'unknown' | 'granted' | 'denied';

/**
 * Some tabs are gated by a per-role capability that only the server can evaluate. The tab's
 * own lightweight call doubles as the probe: success shows the tab, any failure (a 403) hides
 * it, and a probe that can't even start counts as denied. `probe` and `onGranted` must be
 * stable references.
 */
function useCapabilityProbe<T>(
  projectId: number | null | undefined,
  probe: (projectId: number) => Promise<T>,
  onGranted?: (result: T) => void,
): Access {
  const [access, setAccess] = useState<Access>('unknown');
  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setAccess('denied');
      return undefined;
    }
    setAccess('unknown');
    Promise.resolve()
      .then(() => probe(projectId))
      .then((result) => {
        if (cancelled) return;
        // Same tick as the access change, so the tab and what it carries (the pending
        // count) render together rather than the tab appearing bare for a frame.
        onGranted?.(result);
        setAccess('granted');
      })
      .catch(() => {
        if (!cancelled) setAccess('denied');
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, probe, onGranted]);
  return access;
}

/** The Manager Access probe only counts as granted when the server says so explicitly. */
const probeManagerAccess = async (projectId: number) => {
  const res = await managerStoreAccessAPI.access(projectId);
  if (res?.data?.data?.canManage !== true) throw new Error('not granted');
  return res;
};

export default function StoreMasterPage() {
  // The tab lives in the URL so a link can point straight at either half, and so
  // the browser back button steps between them rather than leaving the page. This
  // is also where the retired /master-management/store-categories bookmark lands.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(
    TABS.some((t) => t.key === fromUrl) ? (fromUrl as Tab) : 'stores',
  );

  // A link can open the Stores tab on one store: ?q=<its store code or name>, the free-text
  // value of the drill-down links (utils/drilldown). The Data Hygiene dashboard's rows are
  // such links. The tab starts its search box with it, once, as the page opens.
  // Search's links name the store by code (employeeCode / storeCode), the drill-down
  // links by free text (q): whichever is there starts the box.
  const searchFromLink = (LINK_KEYS.map((k) => searchParams.get(k)).find((v) => v && v.trim()) ?? '').trim();
  // Then q leaves the address (replaced, so Back does not bring it back). Left there it
  // would follow the person to the other tabs, because selectTab copies every address
  // value forward, and it would go on naming a search the person has since changed.
  useEffect(() => {
    if (!LINK_KEYS.some((k) => searchParams.has(k))) return;
    const next = new URLSearchParams(searchParams);
    for (const k of LINK_KEYS) next.delete(k);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // The tab strip and the active tab's primary actions share one row. Rather than
  // hoisting each tab's buttons up here — they differ per tab — the shell exposes
  // a DOM node and each tab portals its own actions into it, so ownership stays
  // with the page that handles them. A callback ref (not useRef) because the
  // children must re-render once the node exists.
  const [actionSlot, setActionSlot] = useState<HTMLDivElement | null>(null);

  // The visible store count, reported up by the embedded StoresPage, shown next to
  // the heading on the Stores tab (it used to sit inline on the standalone page).
  const [storeCount, setStoreCount] = useState<number | null>(null);

  const selectTab = (tab: Tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // Store Requests is gated by the `web.store_requests` page right (its page’s tick) and Manager
  // Access by `web.manager_store_access`; only the server can evaluate either. The tabs' own
  // calls double as their probes (Store Requests' also carries its pending count). The other
  // tabs never wait on them.
  const selectedProjectId = useAuthStore((state) => state.selectedProjectId);
  const [pendingRequests, setPendingRequests] = useState(0);
  const onRequestsGranted = useCallback(
    (res: Awaited<ReturnType<typeof storeRequestsAPI.summary>>) =>
      setPendingRequests(Number(res.data?.data?.pending) || 0),
    [],
  );
  const requestsAccess = useCapabilityProbe(selectedProjectId, storeRequestsAPI.summary, onRequestsGranted);
  const managerAccess = useCapabilityProbe(selectedProjectId, probeManagerAccess);

  const accessFor = useCallback(
    (tab: Tab): Access | null =>
      tab === 'requests' ? requestsAccess
        : tab === 'manager-access' ? managerAccess
          : null,
    [requestsAccess, managerAccess],
  );

  // Landed on a gated tab without its capability (a shared link, or a role change).
  useEffect(() => {
    if (accessFor(activeTab) !== 'denied') return;
    setActiveTab('stores');
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'stores');
    setSearchParams(next, { replace: true });
  }, [accessFor, activeTab, searchParams, setSearchParams]);

  const visibleTabs = TABS.filter((t) => {
    const access = accessFor(t.key);
    return access === null || access === 'granted';
  });
  const current = TABS.find((t) => t.key === activeTab) ?? TABS[0];

  // A saved link to the old Hiring tab: the Hiring dashboard, which decides for itself who may see it.
  if (fromUrl === MOVED_HIRING_TAB) return <Navigate to={dashboardPath('Hiring')} replace />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Stores
          {activeTab === 'stores' && storeCount !== null && (
            <span className="ml-2 text-base font-medium text-gray-400">({storeCount.toLocaleString()})</span>
          )}
        </h1>
        <p className="mt-1 text-sm text-gray-500">{current.blurb}</p>
      </div>

      {/* Tabs left, the active tab's actions right — one row, no empty band. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {t.key === 'requests' && pendingRequests > 0 && (
                  <span className="ml-0.5 rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-800">
                    {pendingRequests}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div ref={setActionSlot} className="flex items-center gap-3" />
      </div>

      {/* Mounted on demand rather than both always: each tab fetches on mount, and
          rendering the hidden one would load the full store roster for a tab the
          user may never open. */}
      {activeTab === 'stores' && (
        <StoresPage embedded actionSlot={actionSlot} onCount={setStoreCount} initialSearch={searchFromLink} />
      )}
      {activeTab === 'categories' && <StoreCategoriesPage embedded actionSlot={actionSlot} />}
      {activeTab === 'trades' && <StoreTradesPage embedded actionSlot={actionSlot} />}
      {activeTab === 'custom-fields' && <StoreCustomFieldsPage embedded actionSlot={actionSlot} />}
      {activeTab === 'requests' && requestsAccess === 'granted' && (
        <StoreRequestsPage actionSlot={actionSlot} onPendingCount={setPendingRequests} />
      )}
      {activeTab === 'manager-access' && managerAccess === 'granted' && (
        <ManagerStoreAccessPage embedded actionSlot={actionSlot} />
      )}
    </div>
  );
}
